import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'
import { Form, Row, Col } from 'react-bootstrap'
import FilteredDropdown from '@components/common/FilteredDropdown/FilteredDropdown.component'
import { ProviderSettingsType } from '@dataTypes/procedureSettings.type'
import { Provider } from '@dataTypes/provider.types'
import { Tooltip } from 'react-tooltip'
import {
  getAllProviders,
  getProviderSettings,
  addOrUpdateProviderSettings,
} from '@services/api/provider.api'
import { ToastComponent, ToastModel } from '@syncfusion/ej2-react-notifications'
import { useAppSelector } from '@store/hooks'

import { logger } from '@utils/safeLogger'

type ProviderInput = {
  providerId: string | null
  value: string
}

export type ProviderSettingsFormHandle = {
  handleSave: () => void
}

const ProviderSettingsForm = forwardRef<ProviderSettingsFormHandle, object>(
  (_, ref) => {
    const [payToInput, setPayToInput] = useState<ProviderInput>({
      providerId: null,
      value: '',
    })
    const [billingToInput, setBillingToInput] = useState<ProviderInput>({
      providerId: null,
      value: '',
    })
    const [referringInput, setReferringInput] = useState<ProviderInput>({
      providerId: null,
      value: '',
    })
    const [renderingInput, setRenderingInput] = useState<ProviderInput>({
      providerId: null,
      value: '',
    })
    const selectedClinic = useAppSelector(
      (state) => state.clinic.selectedClinic,
    )
    const [providersList, setProvidersList] = useState<Provider[]>([])
    const [providerSettings, setProviderSettings] = useState<
      ProviderSettingsType[]
    >([])

    const toastRef = useRef<ToastComponent>(null)
    const toastSettings: ToastModel = {
      title: 'Success',
      content: 'The provider settings have been successfully saved.',
      cssClass: 'e-toast-success',
      icon: 'e-icons e-check',
      showCloseButton: true,
      position: { X: 'Center', Y: 'Top' },
      timeOut: 3000,
    }

    const showToast = (): void => {
      toastRef.current?.show()
    }

    const fetchProviders = async (): Promise<void> => {
      try {
        const response = await getAllProviders()
        if (response.statusCode === 200 && 'providers' in response.data) {
          const providerList = Array.isArray(response.data?.providers)
            ? response.data.providers
            : []

          const filteredProvidersByClinic = providerList.filter(
            (prov) => prov.clinic_id === selectedClinic?.clinic_id,
          )

          setProvidersList(filteredProvidersByClinic as Provider[])
        } else {
          setProvidersList([])
        }
      } catch (error) {
        logger.error('Error fetching providers:', error)
        setProvidersList([])
      }
    }

    const fetchProviderSettings = async (): Promise<void> => {
      try {
        const response = await getProviderSettings()
        if (
          response.statusCode === 200 &&
          'provider_settings' in response.data
        ) {
          setProviderSettings(
            response.data
              .provider_settings as unknown as ProviderSettingsType[],
          )
        } else {
          setProviderSettings([])
        }
      } catch (error) {
        logger.error('Error fetching provider settings:', error)
      }
    }

    useEffect(() => {
      fetchProviders()
      fetchProviderSettings()
    }, [])

    useEffect(() => {
      if (providerSettings && providerSettings.length > 0) {
        const payToProvider = providersList.find(
          (provider) => provider.provider_id === providerSettings[0].pay_to,
        )
        if (payToProvider) {
          setPayToInput({
            providerId: payToProvider.provider_id,
            value: `Dr. ${payToProvider.first_name} ${payToProvider.last_name}`,
          })
        }
        const billingToProvider = providersList.find(
          (provider) => provider.provider_id === providerSettings[0].billing_to,
        )
        if (billingToProvider) {
          setBillingToInput({
            providerId: billingToProvider.provider_id,
            value: `Dr. ${billingToProvider.first_name} ${billingToProvider.last_name}`,
          })
        }
        const referringToProvider = providersList.find(
          (provider) =>
            provider.provider_id === providerSettings[0].referring_to,
        )
        if (referringToProvider) {
          setReferringInput({
            providerId: referringToProvider.provider_id,
            value: `Dr. ${referringToProvider.first_name} ${referringToProvider.last_name}`,
          })
        }
        const renderingToProvider = providersList.find(
          (provider) =>
            provider.provider_id === providerSettings[0].rendering_to,
        )
        if (renderingToProvider) {
          setRenderingInput({
            providerId: renderingToProvider.provider_id,
            value: `Dr. ${renderingToProvider.first_name} ${renderingToProvider.last_name}`,
          })
        }
      }
    }, [providerSettings])

    const saveProviderSettings = async (): Promise<void> => {
      try {
        const providerSettingsToSave = {
          pay_to: payToInput.providerId,
          billing_to: billingToInput.providerId,
          referring_to: referringInput.providerId,
          rendering_to: renderingInput.providerId,
        }
        await addOrUpdateProviderSettings(providerSettingsToSave)
        showToast()
      } catch (error) {
        logger.error('Error saving provider settings:', error)
      }
    }

    useImperativeHandle(ref, () => ({
      handleSave() {
        saveProviderSettings()
      },
    }))

    const renderLabel = (
      label: string,
      tooltipId: string,
      tooltipContent: string,
    ): JSX.Element => (
      <>
        {label}
        <i
          className="mgc mgc_information_fill ms-2"
          data-tooltip-id={tooltipId}
          data-tooltip-content={tooltipContent}
        ></i>
        <Tooltip id={tooltipId} place="top" />
      </>
    )

    return (
      <div className="provider-settings-form">
        <Row className="mb-2">
          <Col>
            <Form.Group>
              <Form.Label>
                {renderLabel(
                  'Billing',
                  'billingToTooltip',
                  'Enter the Billing Provider here.',
                )}
              </Form.Label>
              <FilteredDropdown
                items={providersList}
                labelKey1="first_name"
                labelKey2="last_name"
                value={billingToInput.value}
                placeholder="Enter provider name"
                onChange={(value) =>
                  setBillingToInput({ providerId: null, value })
                }
                onSelect={(provider: Provider) => {
                  const displayName = `Dr. ${provider.first_name} ${provider.last_name}`
                  setBillingToInput({
                    providerId: provider.provider_id,
                    value: displayName,
                  })
                }}
                showIcon={true}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <Form.Group>
              <Form.Label>
                {renderLabel(
                  'Rendering (Optional)',
                  'renderingTooltip',
                  'Enter the Rendering Provider here.',
                )}
              </Form.Label>
              <FilteredDropdown
                items={providersList}
                labelKey1="first_name"
                labelKey2="last_name"
                value={renderingInput.value}
                placeholder="Enter provider name"
                onChange={(value) =>
                  setRenderingInput({ providerId: null, value })
                }
                onSelect={(provider: Provider) => {
                  const displayName = `Dr. ${provider.first_name} ${provider.last_name}`
                  setRenderingInput({
                    providerId: provider.provider_id,
                    value: displayName,
                  })
                }}
                showIcon={true}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <Form.Group>
              <Form.Label>
                {renderLabel(
                  'Referring (Optional)',
                  'referringTooltip',
                  'Enter the Referring Provider here.',
                )}
              </Form.Label>
              <FilteredDropdown
                items={providersList}
                labelKey1="first_name"
                labelKey2="last_name"
                value={referringInput.value}
                placeholder="Enter provider name"
                onChange={(value) =>
                  setReferringInput({ providerId: null, value })
                }
                onSelect={(provider: Provider) => {
                  const displayName = `Dr. ${provider.first_name} ${provider.last_name}`
                  setReferringInput({
                    providerId: provider.provider_id,
                    value: displayName,
                  })
                }}
                showIcon={true}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col>
            <Form.Group>
              <Form.Label>
                {renderLabel(
                  'Pay To (Optional)',
                  'payToTooltip',
                  'Enter the Pay To Provider here.',
                )}
              </Form.Label>
              <FilteredDropdown
                items={providersList}
                labelKey1="first_name"
                labelKey2="last_name"
                value={payToInput.value}
                placeholder="Enter provider name"
                onChange={(value) => setPayToInput({ providerId: null, value })}
                onSelect={(provider: Provider) => {
                  const displayName = `Dr. ${provider.first_name} ${provider.last_name}`
                  setPayToInput({
                    providerId: provider.provider_id,
                    value: displayName,
                  })
                }}
                showIcon={true}
              />
            </Form.Group>
          </Col>
        </Row>
        <ToastComponent ref={toastRef} {...toastSettings} />
      </div>
    )
  },
)

ProviderSettingsForm.displayName = 'ProviderSettingsForm'

export default ProviderSettingsForm
